import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGroupChildren, type Group } from "@/hooks/useGroupChildren";
import { ChevronRight, ChevronLeft, Package } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { buildProductsPath } from "@/lib/groupNameCache";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";

interface PartsHierarchyMenuProps {
  onClose?: () => void;
  onInteraction?: () => void;
}

export function PartsHierarchyMenu({ onClose, onInteraction }: PartsHierarchyMenuProps) {
  const navigate = useNavigate();
  const { settings } = useAppSettings();
  const { flags } = useFeatureFlags();
  const pivotDepth = parseInt(settings.parts_pivot_depth || '4', 10);
  
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<Group[]>([]);
  
  const { groups, isLoading, error } = useGroupChildren(activeGroup);

  // Navigate to products page if group has no children or depth threshold exceeded
  useEffect(() => {
    if (!isLoading && !error && activeGroup && breadcrumb.length > 0) {
      const shouldPivot = groups.length === 0 || breadcrumb.length > pivotDepth;
      if (shouldPivot) {
        const lastGroup = breadcrumb[breadcrumb.length - 1];
        navigate(buildProductsPath(breadcrumb), {
          state: { group: lastGroup, breadcrumb }
        });
        onClose?.();
      }
    }
  }, [groups, isLoading, error, activeGroup, breadcrumb, navigate, onClose, pivotDepth]);

  const getMenuHeight = () => {
    if (isLoading || error) return "200px";
    if (groups.length === 0) return "200px";
    const itemHeight = 52;
    const minHeight = 200;
    const maxHeight = 500;
    const calculatedHeight = groups.length * itemHeight + 32;
    return `${Math.min(Math.max(calculatedHeight, minHeight), maxHeight)}px`;
  };

  const handleGroupClick = (group: Group) => {
    onInteraction?.();
    setBreadcrumb([...breadcrumb, group]);
    setActiveGroup(group.id);
  };

  const handleBack = () => {
    if (breadcrumb.length <= 1) {
      setActiveGroup(null);
      setBreadcrumb([]);
    } else {
      const newBreadcrumb = breadcrumb.slice(0, -1);
      setBreadcrumb(newBreadcrumb);
      setActiveGroup(newBreadcrumb[newBreadcrumb.length - 1]?.id || null);
    }
  };

  const handleNavigateToAncestor = (index: number) => {
    if (index < 0) {
      setActiveGroup(null);
      setBreadcrumb([]);
    } else {
      const newBreadcrumb = breadcrumb.slice(0, index + 1);
      setBreadcrumb(newBreadcrumb);
      setActiveGroup(newBreadcrumb[newBreadcrumb.length - 1]?.id || null);
    }
  };

  const handleViewAllParts = () => {
    if (activeGroup && breadcrumb.length > 0) {
      const lastGroup = breadcrumb[breadcrumb.length - 1];
      navigate('/parts/browse/' + breadcrumb.map(g => g.id).join('/'), {
        state: { group: lastGroup, breadcrumb }
      });
    } else {
      navigate("/parts");
    }
    onClose?.();
  };

  // ─── Vertical Path Mode ───
  if (flags.partsMenuVerticalPath && breadcrumb.length > 0) {
    return (
      <div className="flex" style={{ minWidth: "480px" }}>
        {/* Left column: ancestor path */}
        <div className="w-48 border-r border-border/50 py-2">
          <div className="px-3 pb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Path</span>
          </div>
          <div className="space-y-0.5 px-2">
            <button
              onClick={() => handleNavigateToAncestor(-1)}
              className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
            >
              ← All Parts
            </button>
            {breadcrumb.map((group, index) => (
              <button
                key={group.id}
                onClick={() => index < breadcrumb.length - 1 ? handleNavigateToAncestor(index) : undefined}
                className={cn(
                  "w-full text-left px-2 py-1.5 text-xs rounded transition-colors truncate",
                  index === breadcrumb.length - 1
                    ? "bg-primary/10 text-primary font-medium"
                    : "hover:bg-accent text-muted-foreground hover:text-foreground cursor-pointer"
                )}
                style={{ paddingLeft: `${(index + 1) * 8 + 8}px` }}
              >
                {group.name}
              </button>
            ))}
          </div>
        </div>

        {/* Right column: current children */}
        <div className="flex-1">
          <ScrollArea style={{ height: getMenuHeight() }}>
            <div className="p-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : error ? (
                <div className="px-4 py-8 text-center text-sm text-destructive">{error}</div>
              ) : groups.length > 0 ? (
                <div className="space-y-1">
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => handleGroupClick(group)}
                      className={cn(
                        "w-full flex items-center justify-between gap-2 rounded-lg p-3 text-sm transition-all duration-200",
                        "hover:bg-accent hover:text-accent-foreground group"
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Package className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                        <span className="font-medium truncate">{group.name}</span>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">No subcategories found</div>
              )}
            </div>
          </ScrollArea>
          <div className="border-t border-border/50">
            <button
              onClick={handleViewAllParts}
              className="w-full px-4 py-2 text-sm font-medium text-primary hover:bg-accent transition-colors text-left"
            >
              View All Parts →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Standard layout ───
  return (
    <div className="w-80">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/50">
        <div className="flex items-center justify-between">
          {breadcrumb.length > 0 ? (
            <>
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
              <span className="text-sm font-medium truncate ml-2">
                {breadcrumb[breadcrumb.length - 1]?.name}
              </span>
            </>
          ) : (
            <span className="text-sm font-semibold">Shop RV Parts</span>
          )}
        </div>
        
        {/* Breadcrumb trail */}
        {breadcrumb.length > 0 && (
          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground overflow-x-auto">
            <button
              onClick={() => handleNavigateToAncestor(-1)}
              className="hover:text-foreground whitespace-nowrap"
            >
              Parts
            </button>
            {breadcrumb.map((group, index) => (
              <div key={group.id} className="flex items-center gap-1 shrink-0">
                <ChevronRight className="h-3 w-3" />
                {index === breadcrumb.length - 1 ? (
                  <span className="text-foreground whitespace-nowrap">{group.name}</span>
                ) : (
                  <button
                    onClick={() => handleNavigateToAncestor(index)}
                    className="hover:text-foreground whitespace-nowrap"
                  >
                    {group.name}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <ScrollArea style={{ height: getMenuHeight() }}>
        <div className="p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-sm text-destructive">{error}</div>
          ) : groups.length > 0 ? (
            <div className="space-y-1">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleGroupClick(group)}
                  className={cn(
                    "w-full flex items-center justify-between gap-2 rounded-lg p-3 text-sm transition-all duration-200",
                    "hover:bg-accent hover:text-accent-foreground group"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="font-medium truncate">{group.name}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-transform group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No subcategories found</div>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t border-border/50 space-y-0 divide-y divide-border/50">
        <button
          onClick={handleViewAllParts}
          className="w-full px-4 py-2 text-sm font-medium text-primary hover:bg-accent transition-colors text-left"
        >
          View All Parts →
        </button>
      </div>
    </div>
  );
}
